import {
  AnalyticsEventName,
  AnalyticsPageType,
  ShopifySalesChannel,
  getClientBrowserParameters,
  sendShopifyAnalytics,
} from '@shopify/hydrogen-react';

const queuedEvents = [];

let consentGate = {
  ready: false,
  analyticsAllowed: false,
};

const shop = {
  shopId: process.env.GATSBY_SHOPIFY_SHOP_ID,
  storefrontId: process.env.GATSBY_SHOPIFY_STOREFRONT_ID,
  shopifySalesChannel: ShopifySalesChannel.headless,
  acceptedLanguage: 'EN',
  currency: 'GBP',
};

const getPrivacy = () => {
  if (typeof window === 'undefined') return null;

  const customerPrivacy = window.Shopify?.customerPrivacy;

  if (!customerPrivacy) return null;

  return {
    hasUserConsent: customerPrivacy.analyticsProcessingAllowed(),
    analyticsAllowed: customerPrivacy.analyticsProcessingAllowed(),
    marketingAllowed: customerPrivacy.marketingAllowed(),
    saleOfDataAllowed: customerPrivacy.saleOfDataAllowed(),
  };
};

const captureBrowserContext = () => {
  const {
    uniqueToken: ignoredUniqueToken,
    visitToken: ignoredVisitToken,
    ...browserContext
  } = getClientBrowserParameters();

  return browserContext;
};

const dispatchEvent = async event => {
  if (!consentGate.ready) {
    queuedEvents.push(event);
    return;
  }

  if (!consentGate.analyticsAllowed) return;

  const privacy = getPrivacy();

  if (!privacy?.analyticsAllowed) return;

  /*
   * Read tokens immediately before sending. This matters when an event was
   * queued before the Storefront API tracking request completed.
   */
  const browserParameters = getClientBrowserParameters();

  try {
    await sendShopifyAnalytics({
      eventName: event.eventName,
      payload: {
        ...browserParameters,
        ...event.browserContext,
        ...shop,
        ...privacy,
        ...event.payload,
      },
    });
  } catch (error) {
    console.error('Unable to send Shopify analytics event', error);
  }
};

const track = (eventName, payload = {}) => {
  if (typeof window === 'undefined') return Promise.resolve();

  return dispatchEvent({
    eventName,
    payload,
    browserContext: captureBrowserContext(),
  });
};

export const updateShopifyAnalyticsConsent = ({ ready, analyticsAllowed }) => {
  consentGate = {
    ready,
    analyticsAllowed,
  };

  if (!ready) return;

  if (!analyticsAllowed) {
    queuedEvents.length = 0;
    return;
  }

  const events = queuedEvents.splice(0);

  events.forEach(event => {
    dispatchEvent(event);
  });
};

export const trackShopifyPageView = () => track(AnalyticsEventName.PAGE_VIEW_2);

export const trackShopifyProductView = ({
  productId,
  variantId,
  title,
  variantTitle,
  vendor = 'Display Wizard',
  productType,
  sku,
  price,
}) =>
  track(AnalyticsEventName.PRODUCT_VIEW, {
    pageType: AnalyticsPageType.product,
    resourceId: productId,
    totalValue: Number(price),
    products: [
      {
        productGid: productId,
        variantGid: variantId,
        name: title,
        variantName: variantTitle,
        brand: vendor,
        category: productType,
        sku,
        price: String(price),
        quantity: 1,
      },
    ],
  });

export const trackShopifyCollectionView = ({
  collectionId,
  collectionHandle,
}) =>
  track(AnalyticsEventName.COLLECTION_VIEW, {
    pageType: AnalyticsPageType.collection,
    resourceId: collectionId,
    collectionId,
    collectionHandle,
  });

export const trackShopifySearchView = searchTerm =>
  track(AnalyticsEventName.SEARCH_VIEW, {
    pageType: AnalyticsPageType.search,
    searchString: searchTerm,
  });

export const trackShopifyAddToCart = ({ cartId, products, totalValue }) =>
  track(AnalyticsEventName.ADD_TO_CART, {
    cartId,
    products,
    totalValue: Number(totalValue),
  });
